import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getSessionPolicy } from '@/api/auth';
import { useAuthStore } from '@/auth/authStore';
import { useLockStore } from '@/offline/lockStore';

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'focus'];

/**
 * Signs the person out after the organisation's idle timeout (ToR 4.1.4): the policy comes from the
 * API once per session, activity anywhere in the page resets the clock, and a tab that was asleep
 * is judged by the wall clock when it wakes, so closing the lid does not keep a session alive.
 */
export function useIdleTimeout() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const { data: policy } = useQuery({
    queryKey: ['auth', 'session-policy'],
    queryFn: getSessionPolicy,
    enabled: isAuthenticated,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const minutes = policy?.idleTimeoutMinutes ?? 0;

  useEffect(() => {
    if (!isAuthenticated || minutes <= 0) return;
    const limit = minutes * 60 * 1000;
    let lastActivity = Date.now();
    let timer: number | undefined;

    // Idle locks the screen (ToR 4.3.2) — the session stays, the lock asks for the PIN or the password; the tokens still expire on their own clock.
    const signOut = () => { useLockStore.getState().lock(); };
    const check = () => {
      if (Date.now() - lastActivity >= limit) signOut();
      else timer = window.setTimeout(check, Math.min(limit - (Date.now() - lastActivity) + 250, 60_000));
    };
    const touch = () => { lastActivity = Date.now(); };

    for (const event of ACTIVITY_EVENTS) window.addEventListener(event, touch, { passive: true });
    timer = window.setTimeout(check, Math.min(limit, 60_000));
    return () => {
      for (const event of ACTIVITY_EVENTS) window.removeEventListener(event, touch);
      if (timer) window.clearTimeout(timer);
    };
  }, [isAuthenticated, minutes]);
}
